import type { Except, ValidatedKey } from '@festina-life/core'
import { useField } from 'formik'

import type { RawTextInputProps } from '../../forms/textInput/RawTextInput'
import { TextInput } from '../../forms/textInput/TextInput'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import { withFormikInputDiffing } from '../formikDateInput/withFormikInputDiffing'
import {
  handleDefaultValueClick,
  handleExistingValueClick,
  handleOnBlur,
} from '../formikUtils/formikUtils'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useSimpleValueDifferenceCheck } from '../formikUtils/useSimpleValueDifferenceCheck'
import type { FormikInputPropsForWrapper } from '../types'

type Props = Except<
  RawTextInputProps,
  'defaultValue' | 'onBlur' | 'onChange'
> & {
  name: ValidatedKey
  defaultValue?: string
  onBlur?: (value: string | undefined) => void
} & FormikInputPropsForWrapper

/**
 * FormikTextInput to be used within the scope of Formik forms. Will extract the field's data from Formik and surround the
 * input element with a FormInput to show the field status consistently.
 */
function FormikTextInputInternal({
  name,
  isRequired,
  label,
  defaultValue,
  onDefaultValueClick,
  description,
  disabled,
  onBlur,
  hasRightColumnAlways,
  className,
  existingValueConfig,
  validationText,
  isTableCell,
  ...restProps
}: Props) {
  const [field, meta, { setValue }] = useField(name)
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(disabled)

  const existingValue = useExistingValuesContext()?.getExistingValue?.(
    name,
  ) as string

  const wrapperProps = getWrapperProps({
    isTableCell,
    existingValue,
    inputValueDifferenceFromExistingValue: useSimpleValueDifferenceCheck(name),
    label,
    existingValueConfig,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      defaultValue,
      onDefaultValueClick: () =>
        handleDefaultValueClick({
          defaultValue,
          onBlur,
          onDefaultValueClick,
        }),
      onExistingValueClick: () =>
        handleExistingValueClick({
          existingValue,
          setValue,
          onBlur,
        }),
      htmlFor: name,
      error,
      validationText,
      hasRightColumnAlways,
      className,
    },
  })

  return (
    <TextInput
      wrapperProps={wrapperProps}
      id={name}
      isInError={isInError}
      disabled={isFieldDisabled}
      placeholder={existingValue ?? defaultValue}
      {...field}
      {...restProps}
      onChange={(event) => {
        field.onChange(event)
      }}
      onBlur={(event) => handleOnBlur({ event, field, onBlur })}
    />
  )
}

export const FormikTextInput = withFormikInputDiffing(FormikTextInputInternal)
